/**
 * Legal + contact copy, ported verbatim from the existing live site so the
 * rebuild launches compliant on day one rather than with placeholder links.
 * Blocks: h = heading, p = paragraph, ul = bullet list.
 */

/* The date shown on the live legal pages. Bump this when the policies change. */
export const LEGAL_UPDATED = '17 August 2026';

export const LEGAL = {
  "privacy": {
    "slug": "privacy",
    "title": "Privacy Policy",
    "eyebrow": "Privacy",
    "subtitle": "How Order Flow School collects, uses, shares and protects personal information across the webinar and course funnel.",
    "numbered": true,
    "blocks": [
      {
        "t": "h",
        "v": "Who we are"
      },
      {
        "t": "p",
        "v": "Order Flow School is the trading name used on this website. The person or company operating Order Flow School is responsible for the personal information described in this policy."
      },
      {
        "t": "p",
        "v": "Privacy contact: support@orderflowschool.com"
      },
      {
        "t": "h",
        "v": "Information we collect"
      },
      {
        "t": "ul",
        "v": [
          "Name, email address and, where requested, telephone number.",
          "Webinar registration, attendance, questions and access information.",
          "Order, product, transaction and course-access information.",
          "Messages, support requests and communication preferences.",
          "IP address, browser, device, referral source and security logs.",
          "Cookie or advertising identifiers where an appropriate choice or consent has been provided."
        ]
      },
      {
        "t": "h",
        "v": "Why we use personal information"
      },
      {
        "t": "p",
        "v": "We use personal information to:"
      },
      {
        "t": "ul",
        "v": [
          "Register you for and deliver the webinar you requested.",
          "Send essential access information and timing reminders.",
          "Supply purchased materials and provide customer support.",
          "Process transaction, accounting and tax records.",
          "Protect the website, prevent misuse and resolve technical problems.",
          "Send optional promotional messages only where we have consent or another lawful basis.",
          "Measure or personalise advertising only where the required consent has been obtained."
        ]
      },
      {
        "t": "p",
        "v": "Our lawful bases may include performing a contract, complying with law, legitimate interests in delivering and protecting the service, and consent where consent is required."
      },
      {
        "t": "h",
        "v": "Webinar service messages and optional email"
      },
      {
        "t": "p",
        "v": "Registration confirmations, access links and timing reminders are service messages connected to the webinar you requested. They are kept focused on administering that event."
      },
      {
        "t": "p",
        "v": "Promotional emails are sent only where permitted by law and can be stopped using the unsubscribe link or by contacting us. The webinar SMS program is strictly transactional and is used only for registration confirmation, access-link delivery, schedule changes and start-time reminders. Reply STOP to an applicable SMS at any time. We may retain a minimal suppression record so that we continue to respect your choice."
      },
      {
        "t": "h",
        "v": "SMS consent and mobile information"
      },
      {
        "t": "p",
        "v": "If you optionally select the SMS consent box when registering, Order Flow School may send automated transactional text messages solely to administer that registration, including registration confirmation, access-link delivery, schedule changes and start-time reminders. Message frequency varies. Message and data rates may apply. Reply STOP to opt out or HELP for help. SMS consent is not a condition of registration or purchase."
      },
      {
        "t": "p",
        "v": "We do not sell, rent or share mobile numbers, SMS opt-in data or SMS consent with third parties or affiliates for their marketing or promotional purposes. We share this information only with service providers and telecommunications partners as necessary to deliver and administer the messages you requested, maintain suppression records, prevent misuse and comply with law."
      },
      {
        "t": "h",
        "v": "Service providers and recipients"
      },
      {
        "t": "p",
        "v": "We use providers that help operate the funnel, including:"
      },
      {
        "t": "ul",
        "v": [
          "GoHighLevel/LeadConnector for forms, CRM and communications.",
          "AEvent and Zoom for webinar registration and delivery.",
          "Close and Zapier for lead management and workflow automation.",
          "Calendly for call scheduling.",
          "Discord for restricted internal registration and booking notifications.",
          "Whop and its payment providers for checkout and digital access.",
          "Cloudflare for hosting, security and content delivery.",
          "Professional advisers, regulators or authorities where disclosure is reasonably necessary or legally required."
        ]
      },
      {
        "t": "p",
        "v": "Complete payment-card details are handled by Whop and its payment providers; we do not normally receive them."
      },
      {
        "t": "h",
        "v": "International transfers"
      },
      {
        "t": "p",
        "v": "Some providers may process information outside the United Kingdom. Where a restricted transfer applies, we use an available lawful mechanism, such as an adequacy regulation or approved contractual safeguards."
      },
      {
        "t": "h",
        "v": "Retention"
      },
      {
        "t": "p",
        "v": "We keep information only for as long as reasonably required to deliver the webinar or product, support customers, maintain security, honour opt-outs, meet accounting or legal obligations and resolve claims. We then delete or anonymise it where appropriate. Retention can vary according to the record and applicable law."
      },
      {
        "t": "h",
        "v": "Your rights"
      },
      {
        "t": "p",
        "v": "Depending on the circumstances, you may have rights to access, correct, erase, restrict or receive your information, and to object to processing. You may withdraw consent at any time where processing depends on consent. The right to object to direct marketing is absolute."
      },
      {
        "t": "p",
        "v": "Contact us to exercise a right. You may also complain to the Information Commissioner's Office."
      },
      {
        "t": "h",
        "v": "Security, children and updates"
      },
      {
        "t": "p",
        "v": "We use proportionate technical and organisational measures to protect personal information, although no internet service can promise absolute security."
      },
      {
        "t": "p",
        "v": "The webinar and paid materials are intended for people aged 18 or over. We do not knowingly collect personal information from children."
      },
      {
        "t": "p",
        "v": "We may update this policy when the service or our obligations change. The latest revision date appears above."
      }
    ]
  },
  "terms": {
    "slug": "terms",
    "title": "Terms of Use and Sale",
    "eyebrow": "Website and purchase terms",
    "subtitle": "The terms that apply when you register for a webinar, use this website or purchase digital educational material.",
    "numbered": true,
    "blocks": [
      {
        "t": "h",
        "v": "Operator and acceptance"
      },
      {
        "t": "p",
        "v": "These terms govern the website, webinars and digital educational products supplied under the Order Flow School trading name. The legal seller is the person or company identified at checkout and in the order confirmation. By registering, using the website or placing an order, you agree to these terms and the information shown at checkout. You must be at least 18 and legally capable of entering a contract."
      },
      {
        "t": "h",
        "v": "Webinar registration"
      },
      {
        "t": "p",
        "v": "Registration reserves access to the stated online session. We may make reasonable changes to timing, presenter or delivery method and will notify registered attendees where practicable. Registration does not guarantee a trading, financial, commercial or educational outcome."
      },
      {
        "t": "h",
        "v": "Order Flow School transactional SMS program"
      },
      {
        "t": "p",
        "v": "If you optionally select the SMS consent box on the webinar registration form, you agree to receive automated transactional text messages from Order Flow School solely to administer that registration. These messages are limited to registration confirmation, webinar access-link delivery, schedule changes and start-time reminders."
      },
      {
        "t": "p",
        "v": "Message frequency varies. Message and data rates may apply. Reply STOP to unsubscribe at any time. Reply HELP for help, or email support@orderflowschool.com. Consent to SMS is not a condition of registration or purchase. Mobile carriers are not liable for delayed or undelivered messages."
      },
      {
        "t": "h",
        "v": "Products, orders and payment"
      },
      {
        "t": "p",
        "v": "The description, included materials, total price, currency, taxes, access period and delivery method displayed immediately before purchase form part of your order."
      },
      {
        "t": "p",
        "v": "Checkout and payment are handled through Whop. A contract is formed when the seller identified at checkout accepts payment and issues confirmation. Digital access is supplied through the instructions sent to the email used at checkout. If an offer states \"lifetime access\", this means access for the supported life of that product and platform, subject to these terms and reasonable service changes — not the lifetime of any individual."
      },
      {
        "t": "h",
        "v": "Educational purpose"
      },
      {
        "t": "p",
        "v": "Our material provides general education about order flow and market analysis. It is not personalised investment, financial, legal, accounting or tax advice, and it is not a recommendation to enter, hold or exit a transaction. We do not provide brokerage, portfolio management or trade execution. You remain responsible for your decisions and should obtain independent professional advice where appropriate."
      },
      {
        "t": "h",
        "v": "Trading risk and examples"
      },
      {
        "t": "p",
        "v": "Trading involves a substantial risk of loss and leverage can magnify losses. No course, framework, chart, historical example or testimonial guarantees a result. The Trading Risk Disclosure forms part of these terms."
      },
      {
        "t": "h",
        "v": "Your licence and acceptable use"
      },
      {
        "t": "p",
        "v": "Unless stated otherwise, content is owned by or licensed to Order Flow School. Registration or purchase grants one individual a personal, limited, non-exclusive and non-transferable right to use the applicable material for private educational purposes."
      },
      {
        "t": "p",
        "v": "You must not copy, share, resell, publish, scrape, record, distribute or commercially exploit the material, share account credentials, bypass access controls, introduce malicious code or interfere with the service."
      },
      {
        "t": "h",
        "v": "Third-party services"
      },
      {
        "t": "p",
        "v": "GoHighLevel, AEvent, Zoom, Close, Zapier, Calendly, Discord, Whop and other providers have their own terms and privacy practices. We are not responsible for outages outside our reasonable control, but this does not remove responsibilities or statutory rights that cannot lawfully be excluded."
      },
      {
        "t": "h",
        "v": "Cancellation, refunds and service changes"
      },
      {
        "t": "p",
        "v": "The Refund and Cancellation Policy forms part of these terms. Nothing in these terms limits mandatory consumer rights."
      },
      {
        "t": "p",
        "v": "We may make reasonable updates for accuracy, security or delivery. We will not materially reduce a paid product contrary to its agreed description without an appropriate remedy."
      },
      {
        "t": "h",
        "v": "Liability"
      },
      {
        "t": "p",
        "v": "Nothing excludes liability that cannot legally be excluded, including liability for fraud, fraudulent misrepresentation, death or personal injury caused by negligence, or mandatory consumer rights. Subject to those protections, we are not responsible for trading decisions or market losses arising from how a user chooses to apply educational information."
      },
      {
        "t": "h",
        "v": "Complaints, changes and applicable law"
      },
      {
        "t": "p",
        "v": "Send complaints to support@orderflowschool.com with the email used for registration or purchase and a clear description of the issue."
      },
      {
        "t": "p",
        "v": "We may update these terms prospectively. Applicable law governs these terms, without removing mandatory consumer protections or court rights available where you live."
      }
    ]
  },
  "refunds": {
    "slug": "refunds",
    "title": "Refund and Cancellation Policy",
    "eyebrow": "Cancellations and remedies",
    "subtitle": "How cancellations, statutory rights and requests concerning digital educational products are handled.",
    "blocks": [
      {
        "t": "h",
        "v": "Free webinar"
      },
      {
        "t": "p",
        "v": "Webinar registration is free, so no purchase refund applies. You may ask us to cancel your registration or stop non-essential communications at any time."
      },
      {
        "t": "h",
        "v": "Digital products and immediate access"
      },
      {
        "t": "p",
        "v": "Consumers may have a statutory cancellation period for distance contracts. For digital content supplied immediately, a change-of-mind cancellation right may be lost once supply begins only where the buyer expressly requests immediate supply and acknowledges the effect on that cancellation right."
      },
      {
        "t": "p",
        "v": "If those conditions were not met, or local law provides additional protection, the applicable statutory rights remain available."
      },
      {
        "t": "h",
        "v": "Faulty, inaccessible or misdescribed content"
      },
      {
        "t": "p",
        "v": "Immediate access does not remove rights where digital content is faulty, inaccessible, not as described or not supplied with the required standard of care. Depending on the circumstances and applicable law, a buyer may be entitled to repair, replacement, repeat performance, price reduction or a refund."
      },
      {
        "t": "h",
        "v": "Additional refund guarantee"
      },
      {
        "t": "p",
        "v": "No additional voluntary money-back guarantee is offered unless one is clearly stated in writing at checkout. This does not affect statutory rights."
      },
      {
        "t": "h",
        "v": "How to request help or a remedy"
      },
      {
        "t": "p",
        "v": "Email support@orderflowschool.com with:"
      },
      {
        "t": "ul",
        "v": [
          "Your full name and purchase email address.",
          "Your Whop or order reference.",
          "The product and purchase date.",
          "A clear description of the issue and remedy requested."
        ]
      },
      {
        "t": "p",
        "v": "Approved refunds are returned through the original payment method unless otherwise agreed. Whop or its payment provider may technically issue the payment."
      }
    ]
  },
  "cookies": {
    "slug": "cookies",
    "title": "Cookie Policy",
    "eyebrow": "Storage and embeds",
    "subtitle": "How this funnel uses essential browser storage and third-party registration and checkout services.",
    "blocks": [
      {
        "t": "h",
        "v": "What these technologies do"
      },
      {
        "t": "p",
        "v": "Cookies, local storage and similar technologies can support security, remember a user's choices, deliver embedded services, measure use or support advertising."
      },
      {
        "t": "h",
        "v": "Technologies currently used"
      },
      {
        "t": "ul",
        "v": [
          "Session storage keeps a registration or webinar-access reference on the current device so the confirmation and join flow can continue.",
          "Cloudflare may use security and delivery technologies required to serve and protect the website.",
          "The GoHighLevel registration form and Whop checkout are embedded services and may use security, session and fraud-prevention technologies when opened.",
          "This website does not currently intentionally load Meta Pixel or a separate behavioural analytics script."
        ]
      },
      {
        "t": "h",
        "v": "Essential and optional use"
      },
      {
        "t": "p",
        "v": "Technologies strictly necessary to provide a service you request may operate without consent where the legal exemption applies. Non-essential advertising, behavioural analytics or personalisation technologies will be kept disabled until the required choice or consent has been obtained."
      },
      {
        "t": "h",
        "v": "Managing your choices"
      },
      {
        "t": "p",
        "v": "You can clear cookies and site data using your browser controls. If optional technologies are added, the site will provide a way to accept or reject them and withdraw that choice. Blocking essential storage may prevent registration, checkout, calendar or secure webinar access from working correctly."
      }
    ]
  },
  "risk": {
    "slug": "risk",
    "title": "Trading Risk Disclosure",
    "eyebrow": "Important information",
    "subtitle": "Order Flow School provides education — not personalised financial advice or a promise of trading performance.",
    "blocks": [
      {
        "t": "h",
        "v": "Education, not advice"
      },
      {
        "t": "p",
        "v": "Order Flow School provides general education about order flow and market analysis. It does not provide personalised investment advice, investment management, brokerage, trade execution, tax advice or legal advice. Educational examples are not recommendations to buy or sell."
      },
      {
        "t": "h",
        "v": "Trading can result in substantial losses"
      },
      {
        "t": "p",
        "v": "Trading and investing involve risk. You may lose some or all of the money committed. Leverage can magnify losses and, depending on the product and account terms, may create additional liabilities. Never trade with money you cannot afford to lose."
      },
      {
        "t": "h",
        "v": "No guaranteed method or outcome"
      },
      {
        "t": "p",
        "v": "No course, charting method, platform setup, market concept or execution framework can guarantee profitable trades or prevent losses. Past performance does not reliably predict future results."
      },
      {
        "t": "h",
        "v": "Testimonials and examples"
      },
      {
        "t": "p",
        "v": "Testimonials describe individual experiences with educational materials. They are not typical-results claims, earnings guarantees or evidence that another person will achieve the same outcome. Historical, hypothetical, simulated and replay examples may omit live execution, fees, spreads, slippage, liquidity constraints, psychology and changing market conditions."
      },
      {
        "t": "h",
        "v": "Your responsibility"
      },
      {
        "t": "p",
        "v": "You are responsible for deciding whether trading is appropriate for your circumstances, knowledge, objectives and risk tolerance. Consider obtaining advice from an appropriately authorised, independent financial professional. References to platforms, brokers, exchanges or software do not imply endorsement unless expressly stated."
      }
    ]
  },
  "contact": {
    "slug": "contact",
    "title": "Contact",
    "eyebrow": "Support",
    "subtitle": "Contact details for webinar, course, privacy and purchase support.",
    "blocks": [
      {
        "t": "h",
        "v": "Contact Order Flow School"
      },
      {
        "t": "ul",
        "v": [
          "Trading name: Order Flow School",
          "Support and privacy email: support@orderflowschool.com"
        ]
      },
      {
        "t": "h",
        "v": "\"Help us find your account\""
      },
      {
        "t": "p",
        "v": "For purchase support, include the email used at checkout and the Whop order reference. For webinar support, include the email used to register. Do not send card details, passwords, one-time codes or other sensitive credentials."
      },
      {
        "t": "h",
        "v": "\"Platform support\""
      },
      {
        "t": "p",
        "v": "If the issue concerns Whop, Zoom or another platform, we may ask you to contact that provider directly where only they can access the relevant account or payment record."
      }
    ]
  }
};

export const LEGAL_NAV = [
  { slug: 'privacy',  label: 'Privacy' },
  { slug: 'terms',    label: 'Terms' },
  { slug: 'refunds',  label: 'Refunds' },
  { slug: 'cookies',  label: 'Cookies' },
  { slug: 'risk',     label: 'Trading risk' },
  { slug: 'contact',  label: 'Contact' },
];

export default LEGAL;
